import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { track } from "@/lib/analytics";
import WorkspaceLink from "./WorkspaceLink";
import { appDownloadUrl } from "@/lib/appStore";


export type PublicNavSection = "home" | "how" | "clips";

type PublicNavProps = {
  active: PublicNavSection;
};

/**
 * "Clips" points at /spotlight. The design doc calls the gallery Clips, but the
 * site already had Spotlight serving exactly that content from the same source,
 * so the label is the design's and the page is the one that existed.
 */
const SECTIONS: { id: PublicNavSection; label: string; to: string }[] = [
  { id: "home", label: "Overview", to: "/overview" },
  { id: "how", label: "How it works", to: "/how-it-works" },
  { id: "clips", label: "Clips", to: "/spotlight" },
];

export default function PublicNav({ active }: PublicNavProps) {
  return (
    <m.nav
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="ios-glass fixed top-0 z-50 w-full border-x-0 border-t-0"
      aria-label="Main navigation"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="ManticsCore home">
          <img
            src="/images/logo-m.png"
            alt=""
            width="36"
            height="36"
            className="h-9 w-9 rounded-[10px] object-cover shadow-[0_4px_18px_rgba(27,111,246,0.24)] ring-1 ring-white/10"
          />
          <span className="text-base font-bold tracking-tight text-ios sm:text-lg">
            ManticsCore
          </span>
        </Link>

        <div className="flex-1" />

        <div className="hidden items-center gap-6 md:flex">
          {SECTIONS.map((section) => (
            <Link
              key={section.id}
              to={section.to}
              aria-current={active === section.id ? "page" : undefined}
              className={`border-b-[1.5px] pb-0.5 text-[13.5px] transition-colors ${
                active === section.id
                  ? "border-ios-accent font-semibold text-ios"
                  : "border-transparent text-ios-secondary hover:text-ios"
              }`}
            >
              {section.label}
            </Link>
          ))}
        </div>

        {/* Compact section switcher for narrow viewports. */}
        <div className="hidden min-w-0 items-center gap-0.5 overflow-x-auto rounded-full border border-white/[0.06] bg-black/20 p-1 [scrollbar-width:none] sm:flex md:hidden [&::-webkit-scrollbar]:hidden">
          {SECTIONS.map((section) => (
            <Link
              key={section.id}
              to={section.to}
              aria-current={active === section.id ? "page" : undefined}
              className={`whitespace-nowrap rounded-full border px-2.5 py-1.5 text-[11px] font-semibold transition-colors sm:px-3 sm:text-xs ${
                active === section.id
                  ? "border-white/10 bg-white/[0.07] text-ios"
                  : "border-transparent text-ios-secondary hover:text-ios"
              }`}
            >
              {section.label}
            </Link>
          ))}
        </div>

        <WorkspaceLink className="ml-2 inline-flex min-h-11 shrink-0 items-center rounded-full border border-white/15 px-4 text-[13px] font-semibold text-ios hover:bg-white/10" />

        <m.a
          href={appDownloadUrl()}
          target="_blank"
          rel="noreferrer"
          onClick={() => track("testflight_clicked", { location: `${active}_nav` })}
          whileTap={{ scale: 0.97 }}
          className="hidden shrink-0 rounded-full bg-ios-accent px-[18px] py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 lg:inline-block"
        >
          Get the app
        </m.a>
      </div>
      <div className="flex justify-center gap-6 border-t border-white/[0.06] px-4 py-2 sm:hidden">
        {SECTIONS.map((section) => (
          <Link key={section.id} to={section.to} aria-current={active === section.id ? "page" : undefined}
            className={`py-1 text-xs ${active === section.id ? "font-semibold text-ios" : "text-ios-secondary"}`}>
            {section.label}
          </Link>
        ))}
      </div>
    </m.nav>
  );
}
